import type { PlaycastMessage, PlaycastMessageTarget } from '@playcastdotio/Messaging';

export type GraphHostMessage = PlaycastMessage<PlaycastMessageTarget>;
export interface INodeGraphConnection {
  send(message: GraphHostMessage): boolean;
  subscribe(action: string, listener: (message: GraphHostMessage) => void): () => void;
  onConnection(listener: (connected: boolean) => void): () => void;
  connected(): boolean;
}
interface Pending { action: string; resolve(value: unknown): void; reject(error: Error): void; timer: ReturnType<typeof setTimeout> }

/** No graph requests are queued across a disconnected or replaced Host socket. */
export class NodeServiceGraphChannel {
  private readonly pending = new Map<string, Pending>();
  private readonly listeners = new Set<(message: GraphHostMessage) => void>();
  private readonly subscriptions: Array<() => void> = [];
  public constructor(private readonly connection: INodeGraphConnection, private readonly requestTimeoutMs: number) {}
  public initialize(): void {
    if (this.subscriptions.length) return;
    for (const action of ['macroGraph', 'macroGraphEvent', 'macroGraphSession', 'macroGraphRegistration', 'macroGraphTraceHistory', 'macroGraphSimulation', 'macroGraphTesting', 'macroGraphTestEvent', 'macroSequence', 'macroSequenceEvent']) {
      this.subscriptions.push(this.connection.subscribe(action, message => this.receive(message)));
    }
    this.subscriptions.push(this.connection.onConnection(connected => { if (!connected) this.failPending('graph_host_disconnected_unknown'); }));
  }
  public request<T>(message: GraphHostMessage): Promise<T> {
    const body = message.body.message as { requestId?: string }, id = body.requestId;
    if (!id || this.pending.has(id)) return Promise.reject(new Error('graph_request_id_conflict'));
    if (!this.connection.connected()) return Promise.reject(new Error('graph_host_disconnected_unknown'));
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => { this.pending.delete(id); reject(new Error('graph_reply_unknown')); }, this.requestTimeoutMs);
      this.pending.set(id, { action: String(message.header.action), resolve: resolve as (value: unknown) => void, reject, timer });
      if (!this.connection.send(message)) { this.pending.delete(id); clearTimeout(timer); reject(new Error('graph_host_disconnected_unknown')); }
    });
  }
  public send(message: GraphHostMessage): boolean { return this.connection.connected() && this.connection.send(message); }
  public subscribe(listener: (message: GraphHostMessage) => void): () => void { this.listeners.add(listener); return () => this.listeners.delete(listener); }
  public onConnection(listener: (connected: boolean) => void): () => void { return this.connection.onConnection(listener); }
  public dispose(): void { this.failPending('graph_channel_disposed_unknown'); this.subscriptions.splice(0).forEach(unsubscribe => unsubscribe()); this.listeners.clear(); }
  private receive(message: GraphHostMessage): void {
    const payload = message.body.message as { requestId?: string }, requestId = payload?.requestId;
    const pending = requestId ? this.pending.get(requestId) : undefined;
    if (requestId && message.header.target === 'service' && message.header.isReply && pending?.action === message.header.action) {
      this.pending.delete(requestId); clearTimeout(pending.timer); pending.resolve(payload); return;
    }
    this.listeners.forEach(listener => listener(message));
  }
  private failPending(code: string): void { for (const row of this.pending.values()) { clearTimeout(row.timer); row.reject(new Error(code)); } this.pending.clear(); }
}
